import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Grid,
  IconButton,
  Menu,
  MenuItem,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import useTranslation from "../../../shared/hooks/useTranslation";
import colors from "../../../shared/themes/colors";
import { StatusChip } from "../../../shared/components/chips/StatusChip";
import { ReceivedOffer, OfferStatusCode } from "../models/receivedOffersModels";
import { CompletionTimeCode, WarrantyPeriodCode } from "../../serviceOffers/models/models";
import {
  createReceivedOfferStatusLabelMap,
  createReceivedOfferStatusColorMap,
} from "../utils/receivedOfferStatusMaps";
import { useConfirmation } from "../../../shared/components/confirmation/ConfirmationProvider";
import RejectOfferDialog from "./RejectOfferDialog";
import { dt } from "../../../shared/themes/redesignTokens";
import { getUiLocale } from "../../../shared/utils/relativeTime";

interface OfferComparisonGridProps {
  offers: ReceivedOffer[];
  onAccept: (offerId: string) => void;
  onReject: (offerId: string, reason?: string) => void;
  actionLoading: boolean;
}

const includedServiceKeys: { key: keyof ReceivedOffer; translationKey: string }[] = [
  { key: "includeDiagnostics", translationKey: "serviceOffers:includeDiagnostics" },
  { key: "includeLaborForRepair", translationKey: "serviceOffers:includeLaborForRepair" },
  { key: "includePartsAndMaterials", translationKey: "serviceOffers:includePartsAndMaterials" },
  { key: "includeTestingAndCalibration", translationKey: "serviceOffers:includeTestingAndCalibration" },
  { key: "includeWorkAreaCleanup", translationKey: "serviceOffers:includeWorkAreaCleanup" },
  { key: "includeDetailedServiceReport", translationKey: "serviceOffers:includeDetailedServiceReport" },
];

const formatCurrency = (value?: number) => {
  if (value === undefined || value === null) return "-";
  return `${value.toLocaleString(getUiLocale(), {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}\u20AC`;
};

const canTakeAction = (status: OfferStatusCode) => {
  return status === OfferStatusCode.sent || status === OfferStatusCode.viewed;
};

const OfferComparisonGrid = ({
  offers,
  onAccept,
  onReject,
  actionLoading,
}: OfferComparisonGridProps) => {
  const { translate } = useTranslation(["servicing", "serviceOffers", "common"]);
  const confirm = useConfirmation();
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [menuOffer, setMenuOffer] = useState<ReceivedOffer | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectOfferTarget, setRejectOfferTarget] = useState<ReceivedOffer | null>(null);

  const formatCompletionTime = (code?: CompletionTimeCode, custom?: string) => {
    if (!code) return "-";
    if (code === CompletionTimeCode.custom) return custom || "-";
    return translate(`serviceOffers:completionTimeCodes.${CompletionTimeCode[code]}`);
  };

  const formatWarranty = (code?: WarrantyPeriodCode, custom?: string) => {
    if (!code) return "-";
    if (code === WarrantyPeriodCode.custom) return custom || "-";
    return translate(`serviceOffers:warrantyPeriodCodes.${WarrantyPeriodCode[code]}`);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, offer: ReceivedOffer) => {
    setMenuAnchor(event.currentTarget);
    setMenuOffer(offer);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setMenuOffer(null);
  };

  const handleAccept = (offer: ReceivedOffer) => {
    handleMenuClose();
    confirm({
      title: translate("servicing:acceptOfferTitle"),
      text: translate("servicing:acceptOfferText", { number: offer.internalNumber }),
      confirmButton: translate("servicing:yesAccept"),
      closeButton: translate("common:common.no"),
      severity: "neutral",
      onConfirm: () => onAccept(offer.id),
    });
  };

  const handleRejectOpen = (offer: ReceivedOffer) => {
    handleMenuClose();
    setRejectOfferTarget(offer);
    setRejectDialogOpen(true);
  };

  const handleRejectConfirm = (reason?: string) => {
    if (rejectOfferTarget) {
      onReject(rejectOfferTarget.id, reason);
    }
    setRejectDialogOpen(false);
    setRejectOfferTarget(null);
  };

  return (
    <>
      <Grid container spacing={2}>
        {offers.map((offer) => (
          <Grid item xs={12} md={6} lg={4} key={offer.id}>
            <Card
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                borderRadius: "8px",
                boxShadow: "0 2px 8px 0 rgba(0, 33, 89, 0.08)",
                border: `1px solid ${colors.secondary.gray94}`,
              }}
            >
              <CardContent sx={{ flex: 1, p: 2.5, display: "flex", flexDirection: "column", gap: 1.5 }}>
                {/* Provider Header */}
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    {offer.provider.logoUrl ? (
                      <Box
                        component="img"
                        src={offer.provider.logoUrl}
                        alt={offer.provider.name}
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "8px",
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: "8px",
                          backgroundColor: colors.primary.blue2,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <Box className="text-14-bold" sx={{ color: dt.blue }}>
                          {offer.provider.name?.charAt(0)?.toUpperCase() || "?"}
                        </Box>
                      </Box>
                    )}
                    <Box>
                      <Box className="text-14-bold" sx={{ color: colors.secondary.gray15 }}>
                        {offer.provider.name}
                      </Box>
                    </Box>
                  </Box>
                  <IconButton
                    size="small"
                    onClick={(e) => handleMenuOpen(e, offer)}
                    disabled={actionLoading}
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Box>

                {/* Price */}
                <Box className="heading-2" sx={{ color: colors.secondary.gray15 }}>
                  {formatCurrency(offer.totalEstimatedCost)}
                </Box>

                {/* Timeline & Warranty */}
                <Box sx={{ display: "flex", gap: 4 }}>
                  <Box>
                    <Box
                      className="text-12-regular"
                      sx={{ color: colors.secondary.gray40 }}
                    >
                      {translate("serviceOffers:timeline")}
                    </Box>
                    <Box className="text-14-medium" sx={{ color: colors.secondary.gray20 }}>
                      {formatCompletionTime(offer.completionTimeCode, offer.customCompletionTime)}
                    </Box>
                  </Box>
                  <Box>
                    <Box
                      className="text-12-regular"
                      sx={{ color: colors.secondary.gray40 }}
                    >
                      {translate("serviceOffers:warrantyPeriod")}
                    </Box>
                    <Box className="text-14-medium" sx={{ color: colors.secondary.gray20 }}>
                      {formatWarranty(offer.warrantyPeriodCode, offer.customWarrantyPeriod)}
                    </Box>
                  </Box>
                </Box>

                {/* Includes */}
                <Box>
                  <Box
                    className="text-12-regular"
                    sx={{ color: colors.secondary.gray40, mb: 0.5 }}
                  >
                    {translate("servicing:includes")}
                  </Box>
                  <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                    {includedServiceKeys.map((service) => (
                      <Box
                        key={service.key}
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        {offer[service.key] ? (
                          <CheckIcon sx={{ fontSize: 16, color: colors.primary.green }} />
                        ) : (
                          <CloseIcon sx={{ fontSize: 16, color: colors.primary.red }} />
                        )}
                        <Box className="text-12-regular" sx={{ color: colors.secondary.gray30 }}>
                          {translate(service.translationKey)}
                        </Box>
                      </Box>
                    ))}
                  </Box>
                </Box>

                {/* Status */}
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box className="text-12-regular" sx={{ color: colors.secondary.gray40 }}>
                    {translate("servicing:status")}
                  </Box>
                  <StatusChip
                    status={offer.statusCode}
                    colorMap={createReceivedOfferStatusColorMap()}
                    labelMap={createReceivedOfferStatusLabelMap(translate)}
                  />
                </Box>
              </CardContent>

              {/* Actions */}
              {canTakeAction(offer.statusCode) && (
                <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={() => handleRejectOpen(offer)}
                    disabled={actionLoading}
                    sx={{
                      color: colors.primary.red,
                      borderColor: colors.primary.red,
                      textTransform: "none",
                      "&:hover": {
                        borderColor: colors.primary.red,
                        backgroundColor: colors.backgrounds.red,
                      },
                    }}
                  >
                    {translate("servicing:rejectOffer")}
                  </Button>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={() => handleAccept(offer)}
                    disabled={actionLoading}
                    sx={{
                      backgroundColor: dt.blue,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: `${dt.blue}E6`,
                      },
                    }}
                  >
                    {translate("servicing:acceptOffer")}
                  </Button>
                </CardActions>
              )}
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Menu for card actions */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
      >
        {menuOffer && canTakeAction(menuOffer.statusCode) && [
          <MenuItem key="accept" onClick={() => handleAccept(menuOffer)}>
            {translate("servicing:acceptOffer")}
          </MenuItem>,
          <MenuItem key="reject" onClick={() => handleRejectOpen(menuOffer)}>
            {translate("servicing:rejectOffer")}
          </MenuItem>,
        ]}
      </Menu>

      {/* Reject Dialog */}
      <RejectOfferDialog
        open={rejectDialogOpen}
        offerNumber={rejectOfferTarget?.internalNumber || ""}
        onConfirm={handleRejectConfirm}
        onClose={() => {
          setRejectDialogOpen(false);
          setRejectOfferTarget(null);
        }}
      />
    </>
  );
};

export default OfferComparisonGrid;
